// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component } from '../../utils';
import { get, set } from './utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Measure from 'react-measure';
import { CLS_PREFIX } from '../../constants';
import Button from '../Button';
import FormField from '../FormField';
import Section from '../Section';
import Translate from '../Translate';

import './Form.less';

/**
 * `Form` component.
 */
class Form extends Component {
    constructor(props) {
        super(props);
        this.state = {
            values: this.props.values,
            vertical: this.props.vertical || false,
        };

        this.form = {
            getValue: (name, def) => get(this.state.values, name, def),
            setValue: (name, value) => {
                this.setState(({ values }) => set(values, name, value));
            },
            getErrors: name => get(this.props.errors, name, {}),
            getRequiredMark: () => this.props.requiredMark,
        };
    }

    getChildContext() {
        return {
            form: this.form,
            verticalSection: this.state.vertical,
        };
    }

    componentWillReceiveProps({ values }) {
        if (values !== this.state.values) {
            this.setState({ values });
        }
    }

    handleSubmit = e => {
        e.preventDefault();
        if (this.props.onSubmit) {
            this.props.onSubmit(this.state.values, false);
        }
    };

    handleSubmitClick = () => {
        if (this.props.onSubmit) {
            this.props.onSubmit(this.state.values, false);
        }
    };

    handleApplyClick = () => {
        if (this.props.onSubmit) {
            this.props.onSubmit(this.state.values, true);
        }
    };

    handleResize = ({ bounds }) => {
        const { vertical } = this.props;
        if (vertical !== null) {
            this.setState({ vertical });
            return;
        }

        this.setState({ vertical: bounds.width < 450 });
    };

    renderRequiredLegend({ hideRequiredLegend, requiredMark }) {
        if (hideRequiredLegend || !requiredMark) {
            return '';
        }
        return (
            <span>
                {requiredMark}
                {' '}
                <Translate content="Form.requiredLegend" fallback="Required fields" />
            </span>
        );
    }

    renderSubmitButton({ submitButton, state }) {
        if (!submitButton) {
            return null;
        }
        return createElement(Button, {
            intent: 'primary',
            state: state === 'submit' ? 'loading' : null,
            disabled: !!state,
            onClick: this.handleSubmitClick,
            children: <Translate content="Form.submitButton" fallback="OK" />,
            ...submitButton,
        });
    }

    renderApplyButton({ applyButton, state }) {
        if (!applyButton) {
            return null;
        }
        return createElement(Button, {
            intent: 'primary',
            state: state === 'apply' ? 'loading' : null,
            disabled: !!state,
            onClick: this.handleApplyClick,
            children: <Translate content="Form.applyButton" fallback="Apply" />,
            ...applyButton,
        });
    }

    renderCancelButton({ cancelButton, state }) {
        if (!cancelButton) {
            return null;
        }
        return createElement(Button, {
            state: state === 'cancel' ? 'loading' : null,
            disabled: !!state,
            children: <Translate content="Form.cancelButton" fallback="Cancel" />,
            ...cancelButton,
        });
    }

    render() {
        const {
            baseClassName,
            className,
            children,
            requiredMark,
            hideRequiredLegend,
            values,
            errors,
            submitButton,
            applyButton,
            cancelButton,
            onSubmit,
            additionalButtons,
            footerClassName,
            state,
            ...props
        } = this.props;

        return (
            <Measure onResize={this.handleResize} bounds>
                {({ measureRef }) => (
                    <form
                        ref={measureRef}
                        className={classNames(baseClassName, className)}
                        onSubmit={this.handleSubmit}
                        {...props}
                    >
                        {children}
                        <Section className={classNames(`${baseClassName}__footer`, footerClassName)} title=" ">
                            <FormField label={this.renderRequiredLegend({ hideRequiredLegend, requiredMark })}>
                                {this.renderSubmitButton({ submitButton, state })}
                                {this.renderApplyButton({ applyButton, state })}
                                {additionalButtons}
                                {this.renderCancelButton({ cancelButton, state })}
                            </FormField>
                        </Section>
                    </form>
                )}
            </Measure>
        );
    }
}
Form.propTypes = {
    /**
     * Required mark.
     */
    requiredMark: PropTypes.any,
    /**
     * Hide required legend.
     */
    hideRequiredLegend: PropTypes.bool,
    /**
     * Form values.
     */
    values: PropTypes.object,
    /**
     * Form errors.
     */
    errors: PropTypes.object,
    /**
     * Current active state
     */
    state: PropTypes.oneOf(['submit', 'apply', 'cancel']),
    /**
     * Content of the `Form`.
     */
    children: PropTypes.node,
    /**
     * Submit button visibility or custom configuration
     */
    submitButton: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object,
    ]),
    /**
     * Apply button visibility or custom configuration
     */
    applyButton: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object,
    ]),
    /**
     * Cancel button visibility or custom configuration
     */
    cancelButton: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object,
    ]),
    /**
     * Additional buttons
     */
    additionalButtons: PropTypes.array,
    /**
     * Submit handler
     */
    onSubmit: PropTypes.func,
    /**
     * Additional class name for the buttons container.
     */
    footerClassName: PropTypes.string,
    /**
     * Form responsive view will be switched automatically.
     * Specify this parameter explicitly if you want disable this behaviour and choice vertical or horizontal form view.
     */
    vertical: PropTypes.bool,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};
Form.defaultProps = {
    children: null,
    requiredMark: <span className={`${CLS_PREFIX}form__required`}>{'*'}</span>,
    hideRequiredLegend: false,
    values: {},
    errors: {},
    state: null,
    submitButton: true,
    applyButton: true,
    cancelButton: true,
    additionalButtons: [],
    onSubmit: null,
    className: null,
    footerClassName: null,
    vertical: null,
    baseClassName: `${CLS_PREFIX}form`,
};
Form.childContextTypes = {
    form: PropTypes.object,
    verticalSection: PropTypes.bool,
};

export default Form;



// WEBPACK FOOTER //
// ./components/Form/Form.js